import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import type { FormItem } from './form-view';

export type FormUrlDefaults = Record<string, string | number | boolean | null>;

function getFieldNames(items: FormItem[]): string[] {
  return items.flatMap(item => [
    ...(item.name ? [item.name] : []),
    ...getFieldNames(item.items ?? []),
    ...getFieldNames(item.headerItems ?? []),
    ...getFieldNames(item.footerItems ?? []),
  ]);
}

export function parseFormUrlDefaults(raw: string | null, fieldNames: string[]): FormUrlDefaults {
  if (raw === null) return {};
  const value: unknown = JSON.parse(raw);
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SyntaxError('URL defaults must be a JSON object.');
  }
  const fields = new Set(fieldNames);
  for (const [name, entry] of Object.entries(value)) {
    if (name.split(/[.[\]]+/).some(part => ['__proto__', 'prototype', 'constructor'].includes(part)) ||
      !fields.has(name)) {
      throw new SyntaxError(`Unknown or unsupported form field: ${name}.`);
    }
    if (entry !== null && !['string', 'number', 'boolean'].includes(typeof entry)) {
      throw new SyntaxError(`The ${name} default must be a string, number, boolean or null.`);
    }
    if (typeof entry === 'number' && !Number.isFinite(entry)) {
      throw new SyntaxError(`The ${name} default must be a finite number.`);
    }
  }
  return value as FormUrlDefaults;
}

export function useFormUrlDefaults(items: FormItem[] = [], enabled = true) {
  const location = useLocation();
  const raw = enabled ? new URLSearchParams(location.search).get('defaults') : null;
  const fieldsKey = JSON.stringify(getFieldNames(items));
  const result = useMemo(() => {
    try {
      return { values: parseFormUrlDefaults(raw, JSON.parse(fieldsKey)), error: null };
    } catch (error) {
      if (!(error instanceof SyntaxError)) throw error;
      return { values: {}, error: `Invalid URL defaults: ${error.message}` };
    }
  }, [raw, fieldsKey]);

  useEffect(() => {
    if (result.error) toast.error(result.error, { id: 'invalid-url-defaults' });
  }, [result.error]);

  return result.values;
}
